import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';
import { toast } from 'react-toastify';

// Map backend error codes to user-friendly German messages
const getVoucherErrorMessage = (err) => {
  const code = err?.code || err?.response?.data?.code || '';
  const message = err?.message || err?.response?.data?.message || '';
  
  switch (code) {
    case 'VOUCHER_INVALID_PIN':
      return 'Ungültige PIN';
    case 'VOUCHER_PIN_BLOCKED':
      return 'PIN temporär gesperrt (zu viele Fehlversuche)';
    case 'VOUCHER_NOT_FOUND':
      return 'Gutschein nicht gefunden';
    case 'VOUCHER_NOT_ACTIVE':
      return 'Gutschein ist nicht aktiv';
    case 'VOUCHER_EXPIRED':
      return 'Gutschein ist abgelaufen';
    case 'VOUCHER_EMPTY':
      return 'Gutschein hat kein Guthaben mehr';
    case 'VOUCHER_ALREADY_APPLIED':
      return 'Gutschein wurde bereits eingelöst';
    case 'VOUCHER_NOT_APPLICABLE':
      return 'Gutschein kann nicht angewendet werden';
    case 'VOUCHER_INPUT_MISSING':
      return 'Bitte Gutscheincode eingeben';
    case 'VOUCHER_PIN_REQUIRED':
      return 'Bitte PIN eingeben';
    case 'VOUCHER_MIN_PURCHASE_NOT_REACHED':
      return 'Mindestbestellwert nicht erreicht';
    case 'VOUCHER_RESTRICTED_BRANCH':
      return 'Gutschein ist für diesen Standort nicht gültig';
    case 'VOUCHER_RESTRICTED_ENTERTAINMENT':
      return 'Gutschein ist für dieses Entertainment nicht gültig';
    case 'VOUCHER_RESTRICTED_ENTERTAINMENT_TYPE':
      return 'Gutschein ist für diesen Entertainment-Typ nicht gültig';
    default:
      // Try to extract meaningful message from error
      if (message.toLowerCase().includes('pin')) {
        return 'Ungültige PIN';
      }
      if (message.toLowerCase().includes('not found')) {
        return 'Gutschein nicht gefunden';
      }
      if (message.toLowerCase().includes('expired')) {
        return 'Gutschein ist abgelaufen';
      }
      return 'Fehler beim Einlösen des Gutscheins';
  }
};

const useVoucherRedemption = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, setError, sendRequest } = useHttp();

  const applyVoucher = async (temporaryReservationId, voucherCode, pin = undefined, amount = null) => {
    try {
      const body = { voucherCode };
      if (typeof pin === 'string' && pin.trim().length > 0) {
        body.pin = pin.trim();
      }
      if (amount !== null && amount !== undefined && amount !== '') {
        body.amount = amount;
      }
      const response = await sendRequest({
        url: `/api/v1/temporaryReservation/${temporaryReservationId}/applyVoucher`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body,
      });
      return response;
    } catch (err) {
      setError(err);
      toast.error(getVoucherErrorMessage(err));
      throw err;
    }
  };

  const removeVoucher = async (temporaryReservationId, voucherId) => {
    try {
      const response = await sendRequest({
        url: `/api/v1/temporaryReservation/${temporaryReservationId}/voucher/${voucherId}`,
        method: 'DELETE',
        headers: authCtx.getAuthHeaders(),
      });
      return response;
    } catch (err) {
      setError(err);
      toast.error('Fehler beim Entfernen des Gutscheins');
      throw err;
    }
  };

  return { isLoading, error, applyVoucher, removeVoucher };
};

export default useVoucherRedemption;
